import EventEmitter from 'eventemitter3'
import { oneLine } from 'common-tags'

/**
 * Iframe-situated half of the Choreographer message broker.
 *
 * @class
 */
export class Fosse extends EventEmitter {
  /**
   * @member {WeakMap}
   * @private
   */
  static #instanceMap = new WeakMap()

  /**
   * @member {string}
   * @private
   */
  #verdonOrigin = null

  /**
   * New up a Fosse. Because Fosse is a global singleton, if a Fosse instance
   * has previously been created, we return that instance instead.
   */
  constructor() {
    if (Fosse.#instanceMap.has(Fosse)) {
      return Fosse.#instanceMap.get(Fosse)
    }

    super()

    const currentURL = new URL(globalThis.location.href)
    this.#verdonOrigin = currentURL.searchParams.get('verdonOrigin')

    this.#dispatchLoadEvent()
    this.#listenForMessages()

    Fosse.#instanceMap.set(Fosse, this)
  }

  /**
   * Send a message event to the parent window. Returns the Fosse instance for
   * easier chaining.
   */
  sendMessage(type, payload = undefined) {
    this.#log('Dispatched a "%s" message (payload: %O).', type, payload)
    window.parent.postMessage({ payload, type }, this.#verdonOrigin)
    return this
  }

  /**
   * Notify the parent window's Verdon that Fosse has loaded successfully and is
   * ready to accept messages.
   */
  #dispatchLoadEvent() {
    window.addEventListener('load', () => {
      this.emit(
        'frame:ready',
        {
          fosseBuildTarget: 'staging',
          fosseGitBranch: 'spike/component-proponent',
        },
        window,
      )
    })
  }

  /**
   * Bind a listener for "message" events to the iframe window. Any messages
   * from a known origin with a known shape will be emitted to Fosse's
   * listeners.
   */
  #listenForMessages = () => {
    window.addEventListener(
      'message',
      event => {
        if (event.source !== window.parent) {
          return
        }

        if (event.origin !== this.#verdonOrigin) {
          this.#log(
            'Intercepted a message (%O) from an unknown origin (%s). (Expected "%s.")',
            event.data,
            event.origin,
            this.#verdonOrigin,
          )

          if (process.env.NODE_ENV !== 'production') {
            return
          }
        }

        if (event.data.type == null) {
          this.#log(
            'Intercepted a message (%O) with an unexpected shape. (Missing "data.type.")',
            event.data,
          )

          if (process.env.NODE_ENV === 'production') {
            return
          }
        }

        this.#log(
          'Received a "%s" message (%O) from %s.',
          event.data.type,
          event.data,
          event.origin,
        )

        this.emit(event.data.type, event.data, event.source)
      },
      { capture: false, passive: true },
    )
  }

  /**
   * FIXME: Factor this out into a mixin (or a separate listener plugin?).
   */
  #log = (...args) => {
    const logFn =
      process.env.NODE_ENV === 'production' ? console.debug : console.log

    const groupStyle = oneLine`
      color: #7F4788;
      font-family: -apple-system,BlinkMacSystemFont,sans-serif;
      font-weight: 700;
    `

    const timestampStyle = oneLine`
      color: rgba(126, 126, 126, 0.66);
      font-family: -apple-system,BlinkMacSystemFont,sans-serif;
    `

    console.group(
      '%cCHOREOGRAPHER:FOSSE\t\t%c%s',
      groupStyle,
      timestampStyle,
      new Date().toISOString(),
    )
    logFn(...args)
    console.groupEnd()
  }
}
